import { resolveRuntimeScope, getAllianceSlug } from "./apiBase";

const assetUrl = (name: string) => `${import.meta.env.BASE_URL}assets/${name}`;

export type RankingMode = "power" | "war" | "contribution";
export type RankingPeriod = "total" | "today" | "week";

export type Member = {
  id: string;
  gameUid: string | null;
  gameUidStatus: "bound" | "pending";
  rank: number;
  name: string;
  role: string;
  group: string;
  power: number;
  war: Record<RankingPeriod, number>;
  contribution: Record<RankingPeriod, number>;
  // 入盟时间 = 系统首次采到该成员的日期（member_join 底表）；采集开始前就在盟的人等于首采日。
  joinTime: string;
  joinTimeExact: boolean;
  online: boolean;
  lastSeen: string;
};

export let members: Member[] = [];

export let memberRoleSummary: { label: string; count: number }[] = [];

export let allianceGroups = ["全部分组"];

export const scopeDirectory: Record<string, { label: string; group: string | null }> = {
  alliance: { label: "联盟总体", group: null },
  "qi-ji-ru-feng": { label: "其疾如风", group: "其疾如风" },
  "qi-xu-ru-lin": { label: "其徐如林", group: "其徐如林" },
  "qin-lue-ru-huo": { label: "侵略如火", group: "侵略如火" },
  "dong-ruo-lei-ting": { label: "动若雷霆", group: "动若雷霆" },
  "bu-dong-ru-shan": { label: "不动如山", group: "不动如山" }
};

export type ScopeId = string;

/** 按登录账号所属联盟动态注册分组（御风是自己的黄锋团/…，山河暂不分组）。
 *  管理端登录后从 /wuying/api/admin/me 拿到 scopes，调这里扩充可选项与主题。 */
export function registerScopes(scopes: Array<{ scope: string; label: string }> | undefined) {
  if (!Array.isArray(scopes)) return;
  for (const s of scopes) {
    if (!s || !s.scope) continue;
    if (s.scope === "alliance") continue;
    scopeDirectory[s.scope] = { label: s.label || s.scope, group: s.label || s.scope };
    if (!scopeThemes[s.scope]) scopeThemes[s.scope] = makeGroupTheme(s.label || s.scope);
  }
}

/** 未知分组也安全的主题兜底（赭石土黄档），避免 scopeThemes[scope] 取空崩溃 */
function makeGroupTheme(label: string): ScopeTheme {
  return {
    tone: "mountain",
    code: "RANGE",
    identity: label,
    pulse: "分组视图",
    heroTitle: label,
    heroDescription: label + "专属运营空间。",
    accent: "#8a6d3b",
    accentStrong: "#6b5324",
    accentSoft: "#f3ede2",
    glow: "rgba(138, 109, 59, .26)"
  };
}

export const runtimeScope = resolveRuntimeScope() as ScopeId;

export type ScopeTheme = {
  tone: "alliance" | "wind" | "forest" | "fire" | "thunder" | "mountain";
  code: string;
  identity: string;
  pulse: string;
  heroTitle: string;
  heroDescription: string;
  accent: string;
  accentStrong: string;
  accentSoft: string;
  glow: string;
};

/**
 * Each alliance range keeps the same information architecture as the
 * original wind portal, but gets its own art direction.  The values are
 * consumed by the management hero/login surfaces and by CSS data-scope
 * selectors, so a copied route cannot silently look like another group.
 */
export const scopeThemes: Record<ScopeId, ScopeTheme> = {
  alliance: {
    tone: "alliance",
    code: "NEXUS 01",
    identity: "联盟中枢",
    pulse: "五域协同",
    heroTitle: "联盟中枢，合众成章",
    heroDescription: "联盟运营中枢：统一查看各范围的成员、行动与发布状态。",
    accent: "#2b4a6f",
    accentStrong: "#1e3550",
    accentSoft: "#e9eef5",
    glow: "rgba(43, 74, 111, .30)"
  },
  "qi-ji-ru-feng": {
    tone: "wind",
    code: "RANGE 02",
    identity: "疾行风轨",
    pulse: "快速响应",
    heroTitle: "风驰鎏金，星轨不息",
    heroDescription: "其疾如风专属运营空间：快速响应、灵活转线，保持每一次行动同步。",
    accent: "#9c3a2b",
    accentStrong: "#7d2f23",
    accentSoft: "#f8ece5",
    glow: "rgba(156, 58, 43, .26)"
  },
  "qi-xu-ru-lin": {
    tone: "forest",
    code: "RANGE 03",
    identity: "林阵稳进",
    pulse: "编组有序",
    heroTitle: "徐行成林，稳阵致远",
    heroDescription: "其徐如林专属运营空间：以稳定编组和清晰节奏推进每项行动。",
    accent: "#4f6b52",
    accentStrong: "#3c5340",
    accentSoft: "#eef2e8",
    glow: "rgba(79, 107, 82, .26)"
  },
  "qin-lue-ru-huo": {
    tone: "fire",
    code: "RANGE 04",
    identity: "烈焰攻势",
    pulse: "聚力破阵",
    heroTitle: "烈焰成炬，破阵如虹",
    heroDescription: "侵略如火专属运营空间：聚合攻势、明确目标，行动信息实时到位。",
    accent: "#a8322a",
    accentStrong: "#86261f",
    accentSoft: "#f8ece5",
    glow: "rgba(168, 50, 42, .26)"
  },
  "dong-ruo-lei-ting": {
    tone: "thunder",
    code: "RANGE 05",
    identity: "雷霆决策",
    pulse: "节点先行",
    heroTitle: "雷霆一击，势定千军",
    heroDescription: "动若雷霆专属运营空间：以高效指令和明确节点完成快速决策。",
    accent: "#6f4fa8",
    accentStrong: "#56397f",
    accentSoft: "#f0ebf8",
    glow: "rgba(111, 79, 168, .26)"
  },
  "bu-dong-ru-shan": {
    tone: "mountain",
    code: "RANGE 06",
    identity: "磐石稳守",
    pulse: "岿然不动",
    heroTitle: "不动如山，磐石为基",
    heroDescription: "不动如山专属运营空间：以稳固协防和沉着调度守住每一处阵线。",
    accent: "#8a6d3b",
    accentStrong: "#6b5324",
    accentSoft: "#f3ede2",
    glow: "rgba(138, 109, 59, .26)"
  }
};

export const activeScopeTheme: ScopeTheme = scopeThemes[runtimeScope] || scopeThemes.alliance;

export const isLocalDemo = import.meta.env.DEV
  && ["127.0.0.1", "localhost"].includes(window.location.hostname)
  && new URLSearchParams(window.location.search).get("demo") === "1";

export function applyLocalDemoData() {
  const groupNames = ["其疾如风", "其徐如林", "侵略如火", "动若雷霆", "不动如山"];
  applyCollectionPayload({
    alliance_name: "群星之辉",
    members: Array.from({ length: 36 }, (_, index) => ({
      member_id: `demo-member-${String(index + 1).padStart(3, "0")}`,
      rank: index + 1,
      name: ["风起星澜", "鎏金长歌", "山河入阵", "云上惊雷", "星河策士", "霜刃无声"][index % 6] + (index + 1),
      office: index === 0 ? "盟主" : index < 5 ? "军师" : "成员",
      group: groupNames[index % groupNames.length],
      power: 98600000 - index * 127000,
      contribution_total: 8200000 - index * 31000,
      contribution_today: 128000 - index * 900,
      contribution_week: 716000 - index * 4100,
    })),
    war_rankings: Array.from({ length: 36 }, (_, index) => ({
      member_id: `demo-member-${String(index + 1).padStart(3, "0")}`,
      total: 12600000 - index * 54000,
      today: 238000 - index * 1300,
      week: 1480000 - index * 7600,
      contribution_total: 8200000 - index * 31000,
      contribution_today: 128000 - index * 900,
      contribution_week: 716000 - index * 4100,
    })),
    collection: { run: { run_id: "DEMO-QWEN35-OCR", finished_at_utc: new Date().toISOString() } },
  }, "demo-release");
  applyPortalState({ activities: [], finance: [], guides: [], tips: [{ id: "demo-tip-1", title: "今日行动提示", content: "先确认编组与集合时间，再进入行动频道。遇到临时调整，以管理端最新通知为准。", date: new Date().toISOString().slice(0, 10), status: "已发布" }] });
}

export const collectionMeta = {
  allianceName: "群星之辉",
  scopeLabel: (scopeDirectory[runtimeScope] || scopeDirectory.alliance).label,
  collectedAt: "等待数据中心",
  collectedAtUtc: "",
  runId: "",
  releaseId: "",
  selectedSnapshot: "latest",
  selectedSnapshotTime: ""
};

export function formatCollectionTime(value: unknown) {
  const raw = String(value || "").trim();
  if (!raw) return "已发布";
  const parsed = new Date(raw);
  if (!Number.isFinite(parsed.getTime())) return raw;
  return `${new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(parsed)}（北京时间）`;
}

/**
 * The collector keeps the source alliance label verbatim for auditability.
 * The management UI uses the formal product name only, so source badges such
 * as "［群星A］" must never leak into the public heading.
 */
export function formalAllianceName(value: unknown) {
  const source = String(value || "").trim();
  const withoutSourceBadge = source.replace(/^[［\[]\s*群星[A-Za-zＡ-Ｚａ-ｚ0-9０-９]*\s*[］\]]\s*/, "").trim();
  return withoutSourceBadge || "群星之辉";
}

export type CollectionHistoryEntry = {
  snapshot_id: string;
  run_id: string;
  release_id: string;
  display_time: string;
  collection_time_utc: string;
  published_at_utc: string;
  member_count: number;
  periods: string[];
};

/**
 * Public collection endpoints are also the source of truth for the member
 * name comparison panel.  Keeping the path here avoids coupling the panel to
 * a browser cache: every comparison is built from two server-side protocol
 * snapshots.
 */
export const collectionApiRoot = () => `/alliance-system/api/public/alliances/${getAllianceSlug()}/scopes`;

export type NameSnapshotMember = {
  member_id?: string | number | null;
  game_uid?: string | number | null;
  game_uid_status?: "bound" | "pending" | string | null;
  uid?: string | number | null;
  player_uid?: string | number | null;
  name?: string | null;
  group?: string | null;
  role?: string | null;
  office?: string | null;
  rank?: string | number | null;
  power?: string | number | null;
};

export type NameSnapshot = {
  run_id?: string | null;
  release_id?: string | null;
  display_time?: string | null;
  collection_time_utc?: string | null;
  published_at_utc?: string | null;
  member_count?: number | null;
  members: NameSnapshotMember[];
};

export type NameComparisonChange = "renamed" | "added" | "left";
export type NameComparisonStatus = "confirmed" | "review" | "added" | "left";
export type NameComparisonVerification = "confirmed" | "review";
export type NameComparisonMatch = "member_id" | "game_uid" | "position" | "none";

export type NameComparisonRow = {
  key: string;
  change: NameComparisonChange;
  status: NameComparisonStatus;
  verification: NameComparisonVerification;
  matchBy: NameComparisonMatch;
  memberKey: string;
  memberId: string;
  gameUid: string;
  previousName: string;
  currentName: string;
  group: string;
  previousMember?: NameSnapshotMember;
  currentMember?: NameSnapshotMember;
};

export let collectionHistory: CollectionHistoryEntry[] = [];
export const collectionDataChangedEvent = "alliance-collection-data-changed";

export function setCollectionHistory(entries: CollectionHistoryEntry[]) {
  collectionHistory = Array.isArray(entries) ? entries : [];
}

export function markCollectionSnapshot(runId: string, releaseId: string, time: string) {
  collectionMeta.selectedSnapshot = runId || "latest";
  collectionMeta.selectedSnapshotTime = time || "";
  collectionMeta.releaseId = releaseId || collectionMeta.releaseId;
}

export function notifyCollectionDataChanged() {
  window.dispatchEvent(new Event(collectionDataChangedEvent));
}

function snapshotValue(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

/**
 * Identity fields occasionally contain placeholders when a legacy snapshot
 * predates UID binding.  Treat those values as missing; otherwise two
 * unrelated rows carrying e.g. "pending" could be paired as one member.
 */
const snapshotIdentityPlaceholders = new Set([
  "",
  "0",
  "-",
  "—",
  "na",
  "n/a",
  "none",
  "null",
  "undefined",
  "unknown",
  "pending",
  "未采集",
  "待采集",
  "待绑定"
]);

function snapshotIdentityValue(value: unknown) {
  const normalized = snapshotValue(value);
  if (!normalized) return "";
  const folded = normalized.toLowerCase();
  if (snapshotIdentityPlaceholders.has(folded)) return "";
  // The collector/member fallback uses pending-<scope>-<rank> IDs.  They
  // identify a screen position, not a durable member, so never use them for
  // cross-snapshot identity matching.
  if (/^pending(?:[-_:]|$)/i.test(normalized)) return "";
  return normalized;
}

function snapshotMemberId(member: NameSnapshotMember) {
  return snapshotIdentityValue(member.member_id);
}

function snapshotGameUid(member: NameSnapshotMember) {
  // Do not use nullish coalescing here: older payloads can carry an empty
  // game_uid while still providing player_uid/uid as a valid alias.
  for (const candidate of [member.game_uid, member.player_uid, member.uid]) {
    const normalized = snapshotIdentityValue(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function snapshotMemberName(member: NameSnapshotMember) {
  return snapshotValue(member.name);
}

function snapshotPosition(member: NameSnapshotMember) {
  const group = snapshotValue(member.group);
  const rank = Number(member.rank);
  if (!group || !Number.isFinite(rank)) return "";
  return `${group}::${rank}`;
}

function comparisonMemberKey(member: NameSnapshotMember) {
  return snapshotMemberId(member) || snapshotGameUid(member) || "";
}

/**
 * Return the strongest usable cross-snapshot identity.  Game UID is the
 * in-game identity and therefore takes precedence; member_id is the server
 * fallback for legacy/partially-bound snapshots.
 */
export function stableIdentityKey(member: NameSnapshotMember) {
  return snapshotGameUid(member) || snapshotMemberId(member) || "";
}

function comparisonRow(
  key: string,
  change: NameComparisonChange,
  status: NameComparisonStatus,
  verification: NameComparisonVerification,
  matchBy: NameComparisonMatch,
  previousMember: NameSnapshotMember | undefined,
  currentMember: NameSnapshotMember | undefined
): NameComparisonRow {
  const memberId = snapshotMemberId(currentMember || {}) || snapshotMemberId(previousMember || {});
  const gameUid = snapshotGameUid(currentMember || {}) || snapshotGameUid(previousMember || {});
  return {
    key,
    change,
    status,
    verification,
    matchBy,
    memberKey: memberId || gameUid || "待人工确认",
    memberId,
    gameUid,
    previousName: snapshotMemberName(previousMember || {}),
    currentName: snapshotMemberName(currentMember || {}),
    group: snapshotValue(currentMember?.group) || snapshotValue(previousMember?.group) || "未分组",
    previousMember,
    currentMember
  };
}

/**
 * Compare two protocol snapshots (newest first) without using names as an
 * identity key.  Game UID wins, member_id is the fallback, and conflicting
 * or ambiguous identifiers are paired only as a manual-review candidate. A
 * same-group/same-rank positional pair is likewise never confirmed.
 */
export function compareNameSnapshots(current: NameSnapshot, previous: NameSnapshot): NameComparisonRow[] {
  const currentMembers = Array.isArray(current.members) ? current.members : [];
  const previousMembers = Array.isArray(previous.members) ? previous.members : [];
  const previousById = new Map<string, number[]>();
  const previousByUid = new Map<string, number[]>();
  const currentById = new Map<string, number[]>();
  const currentByUid = new Map<string, number[]>();
  const addIndex = (index: Map<string, number[]>, value: string, row: number) => {
    if (!value) return;
    const rows = index.get(value) || [];
    rows.push(row);
    index.set(value, rows);
  };
  previousMembers.forEach((member, index) => {
    addIndex(previousById, snapshotMemberId(member), index);
    addIndex(previousByUid, snapshotGameUid(member), index);
  });
  currentMembers.forEach((member, index) => {
    addIndex(currentById, snapshotMemberId(member), index);
    addIndex(currentByUid, snapshotGameUid(member), index);
  });
  const uniqueUnmatchedIndex = (
    index: Map<string, number[]>,
    value: string,
    matched: Set<number>
  ) => {
    const rows = value ? index.get(value) : undefined;
    if (!rows || rows.length !== 1 || matched.has(rows[0])) return undefined;
    return rows[0];
  };

  const matchedPrevious = new Set<number>();
  const matchedCurrent = new Set<number>();
  const rows: NameComparisonRow[] = [];

  currentMembers.forEach((member, currentIndex) => {
    const id = snapshotMemberId(member);
    const uid = snapshotGameUid(member);
    const currentIdIsUnique = !id || currentById.get(id)?.length === 1;
    const currentUidIsUnique = !uid || currentByUid.get(uid)?.length === 1;
    const idIndex = currentIdIsUnique
      ? uniqueUnmatchedIndex(previousById, id, matchedPrevious)
      : undefined;
    const uidIndex = currentUidIsUnique
      ? uniqueUnmatchedIndex(previousByUid, uid, matchedPrevious)
      : undefined;
    // UID is authoritative.  If the two keys point to different rows, keep
    // the UID row and downgrade the result to review rather than silently
    // pairing the member_id row.
    const previousIndex = uidIndex !== undefined ? uidIndex : idIndex;
    if (previousIndex === undefined) return;

    matchedCurrent.add(currentIndex);
    matchedPrevious.add(previousIndex);
    const previousMember = previousMembers[previousIndex];
    const previousId = snapshotMemberId(previousMember);
    const previousUid = snapshotGameUid(previousMember);
    const identityConflict = Boolean(
      (uid && previousUid && uid !== previousUid)
      || (id && previousId && id !== previousId)
      || (uidIndex !== undefined && idIndex !== undefined && uidIndex !== idIndex)
      || (uid && !currentUidIsUnique)
      || (id && !currentIdIsUnique)
      || (uid && uidIndex === undefined && idIndex !== undefined && previousUid)
    );
    const previousName = snapshotMemberName(previousMember);
    const currentName = snapshotMemberName(member);
    if (!previousName || !currentName || previousName === currentName) return;
    const matchBy: NameComparisonMatch = uidIndex !== undefined ? "game_uid" : "member_id";
    const verification: NameComparisonVerification = identityConflict ? "review" : "confirmed";
    const status: NameComparisonStatus = identityConflict ? "review" : "confirmed";
    const stableKey = stableIdentityKey(member) || id || uid || `pair-${currentIndex}-${previousIndex}`;
    rows.push(comparisonRow(`rename:${stableKey}`, "renamed", status, verification, matchBy, previousMember, member));
  });

  // When a source omits both stable identifiers, only pair an unambiguous
  // position.  It is a candidate, never a confirmed identity match.
  const currentPositions = new Map<string, number[]>();
  const previousPositions = new Map<string, number[]>();
  currentMembers.forEach((member, index) => {
    if (matchedCurrent.has(index)) return;
    const position = snapshotPosition(member);
    if (!position) return;
    const values = currentPositions.get(position) || [];
    values.push(index);
    currentPositions.set(position, values);
  });
  previousMembers.forEach((member, index) => {
    if (matchedPrevious.has(index)) return;
    const position = snapshotPosition(member);
    if (!position) return;
    const values = previousPositions.get(position) || [];
    values.push(index);
    previousPositions.set(position, values);
  });
  currentPositions.forEach((currentIndexes, position) => {
    const previousIndexes = previousPositions.get(position) || [];
    if (currentIndexes.length !== 1 || previousIndexes.length !== 1) return;
    const currentIndex = currentIndexes[0];
    const previousIndex = previousIndexes[0];
    const currentMember = currentMembers[currentIndex];
    const previousMember = previousMembers[previousIndex];
    matchedCurrent.add(currentIndex);
    matchedPrevious.add(previousIndex);
    const previousName = snapshotMemberName(previousMember);
    const currentName = snapshotMemberName(currentMember);
    if (!previousName || !currentName || previousName === currentName) return;
    rows.push(comparisonRow(`review:${position}`, "renamed", "review", "review", "position", previousMember, currentMember));
  });

  currentMembers.forEach((member, index) => {
    if (matchedCurrent.has(index)) return;
    const key = comparisonMemberKey(member) || `current-${index}`;
    const hasStableKey = Boolean(comparisonMemberKey(member));
    rows.push(comparisonRow(`added:${key}`, "added", hasStableKey ? "added" : "review", hasStableKey ? "confirmed" : "review", "none", undefined, member));
  });
  previousMembers.forEach((member, index) => {
    if (matchedPrevious.has(index)) return;
    const key = comparisonMemberKey(member) || `previous-${index}`;
    const hasStableKey = Boolean(comparisonMemberKey(member));
    rows.push(comparisonRow(`left:${key}`, "left", hasStableKey ? "left" : "review", hasStableKey ? "confirmed" : "review", "none", member, undefined));
  });

  const statusOrder: Record<NameComparisonStatus, number> = { review: 0, confirmed: 1, added: 2, left: 3 };
  return rows.sort((left, right) => {
    const byStatus = statusOrder[left.status] - statusOrder[right.status];
    if (byStatus !== 0) return byStatus;
    return (left.currentName || left.previousName).localeCompare(right.currentName || right.previousName, "zh-CN");
  });
}

type PublishedMember = {
  member_id?: string;
  game_uid?: string | number | null;
  game_uid_status?: "bound" | "pending";
  rank?: number;
  name?: string;
  role?: string;
  office?: string;
  group?: string;
  power?: number;
  contribution_total?: number;
  contribution_today?: number;
  contribution_week?: number;
  join_time?: string;
  join_time_exact?: boolean;
};

type PublishedRanking = {
  member_id?: string;
  name?: string;
  total?: number;
  today?: number;
  week?: number;
  contribution_total?: number;
  contribution_today?: number;
  contribution_week?: number;
};

export function applyCollectionPayload(payload: {
  alliance_name?: string;
  members?: PublishedMember[];
  war_rankings?: PublishedRanking[];
  collection?: { run?: { run_id?: string; finished_at_utc?: string } };
}, releaseId = "") {
  const rankingById = new Map((payload.war_rankings || []).filter((item) => item.member_id).map((item) => [String(item.member_id), item]));
  const rankingByName = new Map((payload.war_rankings || []).map((item) => [String(item.name || ""), item]));
  members = (payload.members || []).map((item, index) => {
    const ranking = (item.member_id ? rankingById.get(String(item.member_id)) : undefined) || rankingByName.get(String(item.name || "")) || {};
    const rank = Number(item.rank) || index + 1;
    return {
      id: String(item.member_id || ranking.member_id || `pending-${rank}`),
      gameUid: item.game_uid == null || String(item.game_uid).trim() === "" ? null : String(item.game_uid),
      gameUidStatus: (item.game_uid_status === "bound" || item.game_uid != null ? "bound" : "pending") as Member["gameUidStatus"],
      rank,
      name: String(item.name || ""),
      role: String(item.role || item.office || "成员"),
      group: String(item.group || "未分组"),
      power: Number(item.power) || 0,
      joinTime: item.join_time ? String(item.join_time) : "",
      joinTimeExact: item.join_time_exact !== false,
      war: {
        total: Number(ranking.total) || 0,
        today: Number(ranking.today) || 0,
        week: Number(ranking.week) || 0
      },
      contribution: {
        total: Number(ranking.contribution_total ?? item.contribution_total) || 0,
        today: Number(ranking.contribution_today ?? item.contribution_today) || 0,
        week: Number(ranking.contribution_week ?? item.contribution_week) || 0
      },
      online: false,
      lastSeen: "本次采集"
    };
  }).filter((item) => item.name);
  const roleCounts = new Map<string, number>();
  for (const member of members) roleCounts.set(member.role, (roleCounts.get(member.role) || 0) + 1);
  memberRoleSummary = [...roleCounts.entries()].map(([label, count]) => ({ label, count }));
  allianceGroups = ["全部分组", ...new Set(members.map((member) => member.group))];
  collectionMeta.allianceName = formalAllianceName(payload.alliance_name || collectionMeta.allianceName);
  collectionMeta.collectedAtUtc = String(payload.collection?.run?.finished_at_utc || "");
  collectionMeta.collectedAt = formatCollectionTime(collectionMeta.collectedAtUtc);
  collectionMeta.runId = String(payload.collection?.run?.run_id || "");
  collectionMeta.releaseId = releaseId;
}

export function dashboardMetrics() {
  return [
    { label: "成员数", value: compactWan(members.length), suffix: "人", compare: "采集范围", change: collectionMeta.scopeLabel },
    { label: "范围总战力", value: compactWan(members.reduce((sum, item) => sum + item.power, 0)), suffix: "", compare: "来源", change: "严格采集" },
    { label: "本周战功", value: compactWan(members.reduce((sum, item) => sum + item.war.week, 0)), suffix: "", compare: "数据状态", change: "已核验" },
    { label: "本周贡献", value: compactWan(members.reduce((sum, item) => sum + item.contribution.week, 0)), suffix: "", compare: "发布范围", change: collectionMeta.scopeLabel }
  ];
}

export type Activity = {
  id: string;
  title: string;
  description: string;
  type: "接龙" | "投票" | "集结";
  status: "进行中" | "草稿" | "已结束";
  time: string;
  starts_at?: string;
  ends_at?: string;
  deadline_at?: string;
  responses: number;
  capacity: number;
  owner: string;
  featured?: boolean;
  image_url?: string;
  options?: string[];
  response_entries?: Array<{
    member_id: string;
    member_name: string;
    value: string;
    created_at: number;
    updated_at?: number;
  }>;
};

export function activityStart(activity: Pick<Activity, "time" | "starts_at" | "deadline_at">) {
  return activity.starts_at || activity.deadline_at || activity.time;
}

export function activityEnd(activity: Pick<Activity, "ends_at">) {
  return activity.ends_at || "";
}

export function activityDeadline(activity: Pick<Activity, "time" | "starts_at" | "deadline_at">) {
  return activityStart(activity);
}

export type ActivityPhase = "草稿" | "即将开始" | "进行中" | "已结束";

export function activityPhase(activity: Pick<Activity, "status" | "time" | "starts_at" | "ends_at" | "deadline_at">, now = Date.now()): ActivityPhase {
  if (activity.status === "草稿") return "草稿";
  if (activity.status === "已结束") return "已结束";
  const start = new Date(activityStart(activity)).getTime();
  const end = new Date(activityEnd(activity)).getTime();
  if (Number.isFinite(end) && now >= end) return "已结束";
  if (Number.isFinite(start) && now < start) return "即将开始";
  return "进行中";
}

export function activityIsCurrent(activity: Pick<Activity, "status" | "time" | "starts_at" | "ends_at" | "deadline_at">, now = Date.now()) {
  const phase = activityPhase(activity, now);
  return phase === "即将开始" || phase === "进行中";
}

export function formatActivityDeadline(value: string) {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return value || "待定";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(parsed);
}

export function shanghaiDeadlineFromInput(value: FormDataEntryValue | null) {
  const local = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return "";
  const parsed = new Date(`${local}:00+08:00`);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
}

export function shanghaiDeadlineInputValue(value?: string, fallbackOffsetHours = 2) {
  const fallback = new Date(Date.now() + fallbackOffsetHours * 60 * 60 * 1000);
  const parsed = value ? new Date(value) : fallback;
  const source = Number.isFinite(parsed.getTime()) ? parsed : fallback;
  return new Date(source.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

export let initialActivities: Activity[] = [];

export type Transaction = {
  id: string;
  date: string;
  type: "收入" | "支出";
  category: string;
  description: string;
  amount: number;
  operator: string;
  status: "已入账" | "待复核";
};

export let initialTransactions: Transaction[] = [];

export function calculateFinanceTotals(transactions: Transaction[]) {
  const income = transactions.filter((item) => item.type === "收入").reduce((sum, item) => sum + item.amount, 0);
  const expense = transactions.filter((item) => item.type === "支出").reduce((sum, item) => sum + item.amount, 0);
  const rewards = transactions.filter((item) => item.category === "军费发放").reduce((sum, item) => sum + item.amount, 0);
  return { income, expense, balance: income - expense, rewards };
}

export type Guide = {
  id: string;
  title: string;
  description: string;
  category: "攻坚" | "成长" | "运营";
  date: string;
  views: number;
  image: string;
  status: "已发布" | "草稿";
  content: string;
  attachments?: Array<{
    name: string;
    url: string;
    kind: "image";
  }>;
};

export const defaultGuideImage = assetUrl("guide-fortress-assault.jpg");
export let initialGuides: Guide[] = [];

export type Tip = {
  id: string;
  title: string;
  content: string;
  date: string;
  status: "已发布" | "草稿";
};

export let initialTips: Tip[] = [];

export function applyPortalState(state: {
  activities?: Activity[];
  finance?: Transaction[];
  guides?: Guide[];
  tips?: Tip[];
}) {
  initialActivities = Array.isArray(state.activities) ? state.activities : [];
  initialTransactions = Array.isArray(state.finance) ? state.finance : [];
  initialGuides = Array.isArray(state.guides) ? state.guides : [];
  initialTips = Array.isArray(state.tips) ? state.tips : [];
}

export const compactNumber = (value: number) => new Intl.NumberFormat("zh-CN").format(value);

/** 首屏那四个大数字用 亿/万（和成员端一致）：15202690294 这种长度在卡片里很占地方 */
export const compactWan = (value: number) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e8) return `${(n / 1e8).toFixed(2).replace(/\.?0+$/, "")} 亿`;
  if (abs >= 1e4) return `${(n / 1e4).toFixed(1).replace(/\.0$/, "")} 万`;
  return new Intl.NumberFormat("zh-CN").format(n);
};
